/**
 * BasePath Configuration
 *
 * Centralized configuration for all basePath-related URLs and paths
 * throughout the POS application. This ensures consistency across
 * client-side, server-side, and API route configurations.
 */

/**
 * Get the configured basePath from environment variables
 * Returns empty string if no basePath is configured to avoid double basePath issues
 * This aligns with Next.js basePath configuration in next.config.ts
 */
export const getBasePath = (): string => {
  // Check for NEXT_PUBLIC_CASHLESS_DASHBOARD_BASE_PATH first (client and server)
  if (process.env.NEXT_PUBLIC_CASHLESS_DASHBOARD_BASE_PATH) {
    const basePath = process.env.NEXT_PUBLIC_CASHLESS_DASHBOARD_BASE_PATH;
    // Ensure basePath starts with / and doesn't end with /
    if (basePath === "/") return "";
    return basePath.startsWith("/") ? basePath : `/${basePath}`;
  }

  // During build time or when basePath is not configured, return empty string
  // This prevents build failures while ensuring runtime validation
  if (process.env.NODE_ENV === "production" && typeof window === "undefined") {
    console.warn("BasePath not configured, using empty string for build");
    return ""; // Build-time fallback
  }

  // Only log warning at runtime in browser, not during build
  if (typeof window !== "undefined") {
    console.warn(
      "Base path is not configured - NEXT_PUBLIC_CASHLESS_DASHBOARD_BASE_PATH:",
      process.env.NEXT_PUBLIC_CASHLESS_DASHBOARD_BASE_PATH,
    );
  }

  return ""; // Server-side fallback
};

/**
 * Get the full URL for a given path with basePath prepended
 */
export const getFullPath = (path: string): string => {
  const basePath = getBasePath();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${basePath}${cleanPath}`;
};

/**
 * Common paths used throughout the application
 */
export const PATHS = {
  // Authentication
  LOGIN: "/auth/login",
  LOGOUT: "/auth/logout",

  // Main app
  DASHBOARD: "/dashboard",

  // Static assets (if needed)
  STATIC: "/staff-static",
} as const;

/**
 * Get full URLs for common paths
 * When basePath is empty, paths will be relative to root
 * When basePath is set, paths will be prefixed with basePath
 */
export const getAuthPaths = () => {
  return {
    LOGIN_URL: PATHS.LOGIN,
    LOGOUT_URL: PATHS.LOGOUT,
    DASHBOARD_URL: PATHS.DASHBOARD,
  };
};

export const LOGIN_PAGE_URL = getAuthPaths()?.LOGIN_URL;

/**
 * Check if a given pathname matches any of the known basePath variants
 */
export const isBasePathRoute = (pathname: string): boolean => {
  const basePath = getBasePath();
  return pathname.startsWith(basePath) || pathname === basePath.slice(1); // Handle both /pos and pos
};

/**
 * Normalize a pathname to work with basePath routing
 */
export const normalizePathname = (pathname: string): string => {
  const basePath = getBasePath();

  // If pathname already includes basePath, return as-is
  if (pathname.startsWith(basePath)) {
    return pathname;
  }

  // If pathname is relative, prepend basePath
  if (!pathname.startsWith("/")) {
    return `${basePath}/${pathname}`;
  }

  // If pathname is absolute but doesn't include basePath, prepend it
  return `${basePath}${pathname}`;
};

/**
 * Debug utility to log current basePath configuration
 */
export const debugBasePath = (): void => {
  if (process.env.NODE_ENV === "development") {
    console.log("[BasePath Debug]", {
      NEXT_PUBLIC_CASHLESS_DASHBOARD_BASE_PATH:
        process.env.NEXT_PUBLIC_CASHLESS_DASHBOARD_BASE_PATH,
      resolved: getBasePath(),
      isServer: typeof window === "undefined",
      paths: getAuthPaths(),
    });
  }
};
